import React, { useState } from 'react';
import { createPortal } from 'react-dom';

import { useApprovalGuideModal } from '@app/providers/ApprovalGuideModalProvider';
import { useApprovalGuideQuery } from '@features/approval-guide/hooks/useApprovalGuideQuery';
import ChevronRightIcon from '@shared/assets/images/chevron-right_icon.svg';

import styles from './ApprovalLineGuideModal.module.scss';

function ApprovalLineGuideModal() {
  const { isOpen, docTitle, close } = useApprovalGuideModal();
  const [openSections, setOpenSections] = useState({
    basicOrder: true,
  });

  const { data, isLoading, isError, error } = useApprovalGuideQuery(
    {
      enabled: isOpen,
    },
    docTitle,
  );

  const orders = data?.orders || [];
  const guides = data?.guides || [];

  if (!isOpen) {
    return null;
  }

  const toggleSection = (sectionKey) => {
    setOpenSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  // '●' 또는 빈 문자열만 반환하도록 수정
  const renderCheckMark = (isRequired) => (isRequired ? '●' : '');

  return createPortal(
    <div className={styles.overlay} onClick={close}>
      <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
        <div className={styles.contentsWrapper}>
          <button type="button" className={styles.closeButton} onClick={close} aria-label="닫기">
            ×
          </button>
          <div className={styles.contentContainer}>
            <h2 className={styles.title}>결재라인 안내</h2>

            {isLoading && (
              <div className={styles.loadingContainer}>
                <div className={styles.circularProgress} role="status" />
                <div className={styles.loadingMessage}>가이드라인 로딩 중...</div>
              </div>
            )}
            {isError && (
              <div className={styles.errorMessage}>
                {error instanceof Error
                  ? error.message
                  : '가이드라인을 불러오는 중 오류가 발생했습니다.'}
              </div>
            )}
            {!isLoading && !isError && data && (
              <>
                <h3
                  className={`${styles.subTitle} ${styles.clickableSubTitle}`}
                  onClick={() => toggleSection('basicOrder')}
                >
                  <span>결재라인 기본 순서</span>
                  <img
                    src={ChevronRightIcon}
                    alt="토글"
                    className={`${styles.chevron} ${openSections.basicOrder ? styles.chevronOpen : ''}`}
                  />
                </h3>
                {openSections.basicOrder && (
                  <div className={styles.tableContainer}>
                    <table className={styles.guideTable}>
                      <thead>
                        <tr>
                          <th>순서</th>
                          <th>구분</th>
                          <th>설명</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map((item) => (
                          <tr key={item.idx}>
                            <td className={styles.centeredCell}>{item.stepOrder}</td>
                            <td>{item.stepName}</td>
                            <td className={styles.descriptionCell}>{item.description}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <h3 className={styles.subTitle}>
                  <span>양식별 결재라인</span>
                </h3>
                <div className={styles.formGuideTableContainer}>
                  <table className={styles.formGuideTable}>
                    <thead>
                      <tr>
                        <th rowSpan="2">해당 양식</th>
                        <th colSpan="6">결재라인</th>
                        <th rowSpan="2">참조</th>
                      </tr>
                      <tr>
                        <th>검토</th>
                        <th>1차</th>
                        <th>2차</th>
                        <th>3차</th>
                        <th>
                          합의
                          <br />
                          (경영기획본부장)
                        </th>
                        <th>대표이사</th>
                      </tr>
                    </thead>
                    <tbody>
                      {guides.length > 0 ? (
                        guides.map((guide) => (
                          <tr key={guide.idx}>
                            <td>{guide.guideTitle}</td>
                            <td>{guide.reviewerDept || ''}</td>
                            <td className={styles.centeredCell}>
                              {renderCheckMark(guide.firstStepRequired)}
                            </td>
                            <td className={styles.centeredCell}>
                              {renderCheckMark(guide.secondStepRequired)}
                            </td>
                            <td className={styles.centeredCell}>
                              {renderCheckMark(guide.thirdStepRequired)}
                            </td>
                            <td className={styles.centeredCell}>
                              {renderCheckMark(guide.agreementRequired)}
                            </td>
                            <td className={styles.centeredCell}>
                              {renderCheckMark(guide.ceoRequired)}
                            </td>
                            <td>{guide.recipient}</td>
                          </tr>
                        ))
                      ) : (
                        <tr className={styles.emptyRow}>
                          <td />
                          <td />
                          <td className={styles.centeredCell} />
                          <td className={styles.centeredCell} />
                          <td className={styles.centeredCell} />
                          <td className={styles.centeredCell} />
                          <td className={styles.centeredCell} />
                          <td />
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {guides.length === 0 && (
                  <p className={styles.emptyTableMessage}>일치하는 양식이 없습니다.</p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default ApprovalLineGuideModal;
